import { QueuedFile } from '@/types/uploadQueue';
import {
  debouncedSaveToStorage,
  loadQueueFromStorage,
} from '@/utils/uploadQueueUtils';
import { toast } from '@/utils/toast';
import { api } from '@/services/http';
import { sessionManager } from '@/services/sessionManager';
import { getUploadLaunchIntervalMs, getUploadMaxConcurrent } from '@/utils/uploadPreferences';

import React, { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  UploadQueueProvider as ContextProvider,
  useUploadQueue,
} from '../contexts/UploadQueueContext';

interface UploadQueueProviderProps {
  children: React.ReactNode;
}

// Upload logic component
const UploadQueueLogic: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const context = useUploadQueue();
  const {
    state,
    updateFileProgress,
    updateFileStatus,
    setCurrentUploadIndex,
    addError,
    pauseUpload,
    startUpload,
  } = context;

  const queryClient = useQueryClient();
  const uploadAbortControllersRef = useRef<
    Map<
      string,
      { controller: AbortController; reason: 'pause' | 'cancel' | null }
    >
  >(new Map());
  const activeUploadIdsRef = useRef<Set<string>>(new Set());
  const processUploadQueueRef = useRef<(() => void) | null>(null);
  const isSchedulingStartsRef = useRef(false);
  const stateRef = useRef(state);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const wasUploadingRef = useRef(false);
  const hasShownBatchSummaryRef = useRef(false);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Save state to localStorage on changes (longer debounce during upload)
  useEffect(() => {
    const delay = state.isUploading ? 4000 : 1000;
    debouncedSaveToStorage(state, delay);
  }, [state]);

  // Upload single file with progress tracking
  const uploadSingleFile = useCallback(
    async function uploadSingleFileInner(
      queuedFile: QueuedFile,
      attempt = 0
    ): Promise<void> {
      const { id: fileId, file } = queuedFile;

      try {
        // Update status to uploading
        updateFileStatus(fileId, 'uploading');

        // Create FormData
        const formData = new FormData();
        formData.append('file', file);
        // study_id no longer sent as it's now in JWT token

        // Create AbortController for this upload (per-file)
        const controller = new AbortController();
        uploadAbortControllersRef.current.set(fileId, {
          controller,
          reason: null,
        });

        // Upload with progress tracking to whitelisted study files endpoint
        // This widget is now used only for file-upload section
        try {
          await api.post('/v1/whitelisted_study_files', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            signal: controller.signal,
          });
        } catch (error: unknown) {
          let errorMessage = 'Upload failed';
          
          if (
            error &&
            typeof error === 'object' &&
            'response' in error &&
            (error as { response?: unknown }).response
          ) {
            // Handle axios error response
            const errorResponse = (error as { response?: unknown }).response as {
              data?: unknown;
              status?: unknown;
              statusText?: unknown;
            };
            const errorData = errorResponse.data as
              | { error?: unknown }
              | undefined;
            if (errorData?.error) {
              // Handle nested error object structure
              if (
                typeof errorData.error === 'object' &&
                errorData.error !== null
              ) {
                const errorObj = errorData.error as Record<string, unknown>;
                const title =
                  typeof errorObj.title === 'string' ? errorObj.title : undefined;
                const details =
                  typeof errorObj.details === 'string'
                    ? errorObj.details
                    : undefined;

                if (title && details) {
                  errorMessage = `${title}: ${details}`;
                } else if (title) {
                  errorMessage = title;
                } else if (details) {
                  errorMessage = details;
                }
              } else if (typeof errorData.error === 'string') {
                errorMessage = errorData.error;
              }
            } else {
              const status =
                typeof errorResponse.status === 'number'
                  ? errorResponse.status
                  : 'unknown';
              const statusText =
                typeof errorResponse.statusText === 'string'
                  ? errorResponse.statusText
                  : '';
              errorMessage = `Upload failed: ${status} ${statusText}`.trim();
            }
          } else if (
            error &&
            typeof error === 'object' &&
            'message' in error &&
            typeof (error as { message?: unknown }).message === 'string'
          ) {
            errorMessage = (error as { message: string }).message;
          }

          throw new Error(errorMessage);
        }

        // Update progress to 100% and mark as completed
        updateFileProgress(fileId, 100);
        updateFileStatus(fileId, 'completed');

        // Invalidate queries to refresh data
        // Since this widget is now used only for whitelisted files, invalidate both
        queryClient.invalidateQueries({ queryKey: ['studyFiles'] });
        queryClient.invalidateQueries({ queryKey: ['whitelistedStudyFiles'] });
      } catch (error: unknown) {
        const isReauthError =
          error instanceof Error &&
          error.message.toLowerCase().includes('reauthentication required');

        if (isReauthError && attempt < 1) {
          updateFileStatus(fileId, 'pending');
          try {
            await sessionManager.ensureValidSession('upload-reauth');
            return uploadSingleFileInner(queuedFile, attempt + 1);
          } catch {
            // Fall through to error handling when reauth fails.
          }
        }

        const isOffline =
          typeof navigator !== 'undefined' && navigator.onLine === false;
        const isNetworkError =
          error instanceof Error &&
          (error.message.toLowerCase().includes('network') ||
            error.message.toLowerCase().includes('timeout') ||
            error.message.toLowerCase().includes('failed to fetch'));

        if (isOffline || sessionManager.isReauthRequired()) {
          updateFileStatus(fileId, 'pending');
          pauseUpload();
          return;
        }

        const abortReason =
          uploadAbortControllersRef.current.get(fileId)?.reason ?? null;
        const isCancelledError =
          error instanceof Error &&
          (error.name === 'AbortError' || error.name === 'CanceledError');

        if (isCancelledError) {
          // Distinguish between pause vs cancel semantics.
          if (abortReason === 'pause') {
            updateFileStatus(fileId, 'pending');
          } else {
            updateFileStatus(fileId, 'cancelled');
          }
        } else {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown upload error';
          updateFileStatus(fileId, 'error', errorMessage);
          addError({
            fileId,
            fileName: file.name,
            error: errorMessage,
            timestamp: new Date(),
          });

          // Show error toast
          if (!isNetworkError) {
            toast.error(`Failed to upload ${file.name}`, {
              description: errorMessage,
            });
          }
        }
        throw error;
      } finally {
        uploadAbortControllersRef.current.delete(fileId);
      }
    },
    [updateFileStatus, updateFileProgress, addError, queryClient, pauseUpload]
  );

  const startOneUpload = useCallback(
    (queuedFile: QueuedFile) => {
      const fileId = queuedFile.id;
      if (activeUploadIdsRef.current.has(fileId)) return;

      activeUploadIdsRef.current.add(fileId);
      // Notify session manager that a long-running operation has started
      sessionManager.startOperation();

      const currentIndex = state.files.findIndex((f: QueuedFile) => f.id === fileId);
      if (currentIndex >= 0) setCurrentUploadIndex(currentIndex);

      void uploadSingleFile(queuedFile)
        .catch(() => {
          // Errors are reflected via status + error state.
        })
        .finally(() => {
          activeUploadIdsRef.current.delete(fileId);
          // Notify session manager that the operation has completed
          sessionManager.endOperation();
          processUploadQueueRef.current?.();
        });
    },
    [setCurrentUploadIndex, state.files, uploadSingleFile]
  );

  const processUploadQueue = useCallback(() => {
    if (isSchedulingStartsRef.current) return;
    if (!state.isUploading || state.isPaused) return;

    const maxConcurrent = getUploadMaxConcurrent();
    const startIntervalMs = getUploadLaunchIntervalMs();
    const activeCount = activeUploadIdsRef.current.size;
    const hasPending = state.files.some(f => f.status === 'pending');

    if (state.files.length > 0 && !hasPending && activeCount === 0) {
      // Queue is done.
      pauseUpload();
      return;
    }

    const availableSlots = Math.max(0, maxConcurrent - activeCount);
    if (availableSlots === 0) return;

    isSchedulingStartsRef.current = true;

    const scheduleStarts = async () => {
      try {
        for (let i = 0; i < availableSlots; i += 1) {
          const current = stateRef.current;
          if (!current.isUploading || current.isPaused) return;

          const nextFile =
            current.files.find(
              f =>
                f.status === 'pending' && !activeUploadIdsRef.current.has(f.id)
            ) || null;
          if (!nextFile) return;

          startOneUpload(nextFile);

          if (startIntervalMs > 0 && i < availableSlots - 1) {
            await new Promise<void>(resolve => setTimeout(resolve, startIntervalMs));
          }
        }
      } finally {
        isSchedulingStartsRef.current = false;
      }
    };

    void scheduleStarts();
  }, [pauseUpload, startOneUpload, state.files, state.isPaused, state.isUploading]);

  // Store the function in ref to avoid useEffect dependency issues
  processUploadQueueRef.current = processUploadQueue;

  // Start processing when upload begins or when file status/progress changes
  useEffect(() => {
    if (state.isUploading && !state.isPaused) {
      // Small delay to ensure state has updated
      setTimeout(() => {
        processUploadQueueRef.current?.();
      }, 50);
    }
  }, [state.isUploading, state.isPaused, state.files.length, state.statusChangeCount]);

  useEffect(() => {
    const handleOffline = () => {
      if (state.isUploading) {
        wasUploadingRef.current = true;
        uploadAbortControllersRef.current.forEach(value => {
          value.reason = 'pause';
          value.controller.abort();
        });
        pauseUpload();
      }
    };
    const handleOnline = () => {
      if (wasUploadingRef.current) {
        wasUploadingRef.current = false;
        startUpload();
      }
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [pauseUpload, startUpload, state.isUploading]);

  useEffect(() => {
    const unsubscribeRequired = sessionManager.on('reauth-required', () => {
      if (state.isUploading) {
        wasUploadingRef.current = true;
        uploadAbortControllersRef.current.forEach(value => {
          value.reason = 'pause';
          value.controller.abort();
        });
        pauseUpload();
      }
    });
    const unsubscribeSuccess = sessionManager.on('reauth-success', () => {
      if (wasUploadingRef.current) {
        wasUploadingRef.current = false;
        startUpload();
      }
    });
    const unsubscribeCancelled = sessionManager.on('reauth-cancelled', () => {
      wasUploadingRef.current = false;
    });

    return () => {
      unsubscribeRequired();
      unsubscribeSuccess();
      unsubscribeCancelled();
    };
  }, [pauseUpload, startUpload, state.isUploading]);

  // Cancel uploads when upload is cancelled
  useEffect(() => {
    if (!state.isUploading && uploadAbortControllersRef.current.size > 0) {
      const reason: 'pause' | 'cancel' = state.isPaused ? 'pause' : 'cancel';
      uploadAbortControllersRef.current.forEach(value => {
        value.reason = reason;
        value.controller.abort();
      });
    }
  }, [state.isUploading]);

  // Show summary toast when a batch completes (avoid per-file spam for large queues).
  useEffect(() => {
    const total = state.files.length;
    if (total === 0) {
      hasShownBatchSummaryRef.current = false;
      return;
    }

    // Single pass to compute counts and hasPendingOrUploading
    let completedCount = 0;
    let errorCount = 0;
    let cancelledCount = 0;
    let hasPendingOrUploading = false;
    for (const f of state.files) {
      if (f.status === 'pending' || f.status === 'uploading') {
        hasPendingOrUploading = true;
        break; // No need to count when batch is still running
      } else if (f.status === 'completed') {
        completedCount += 1;
      } else if (f.status === 'error') {
        errorCount += 1;
      } else if (f.status === 'cancelled') {
        cancelledCount += 1;
      }
    }

    if (hasPendingOrUploading) {
      hasShownBatchSummaryRef.current = false;
      return;
    }

    if (hasShownBatchSummaryRef.current) return;
    hasShownBatchSummaryRef.current = true;

    // If everything was cancelled, don't show a misleading summary toast.
    if (cancelledCount === total && total > 0) return;

    if (completedCount > 0 && errorCount === 0) {
      toast.success('All files uploaded successfully!', {
        description: `${completedCount} files uploaded`,
      });
      return;
    }

    if (completedCount > 0 && errorCount > 0) {
      toast.warning('Upload completed with some errors', {
        description: `${completedCount} uploaded, ${errorCount} failed`,
      });
      return;
    }

    if (errorCount > 0 && completedCount === 0) {
      toast.error('Upload failed', {
        description: `${errorCount} files failed to upload`,
      });
    }
  }, [state.files]);

  // Cleanup on unmount
  useEffect(() => {
    const refreshTimeout = refreshTimeoutRef.current;
    return () => {
      uploadAbortControllersRef.current.forEach(value => value.controller.abort());
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
    };
  }, []);

  return <>{children}</>;
};

// Main provider component
export const UploadQueueProvider: React.FC<UploadQueueProviderProps> = ({
  children,
}) => {
  const [initialState, setInitialState] = React.useState<
    Partial<import('../types/uploadQueue').UploadQueueState> | undefined
  >(undefined);
  const [isLoading, setIsLoading] = React.useState(true);

  // Load initial state from localStorage
  useEffect(() => {
    const loadInitialState = async () => {
      try {
        const savedState = loadQueueFromStorage();
        setInitialState(savedState || undefined);
      } catch {
        setInitialState(undefined);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialState();
  }, []);

  if (isLoading) {
    // Hide loading state - just render nothing while restoring from localStorage
    return null;
  }

  return (
    <ContextProvider initialState={initialState}>
      <UploadQueueLogic>{children}</UploadQueueLogic>
    </ContextProvider>
  );
};
